/// <amd-module name="Build/Extensions/GitHubEnterprise/ConnectionSelection" />
import * as React from "react";

import {
    Actions,
    IServiceConnectionRetrieverService,
    noConnectionsPath,
    ServiceConnectionInfo
} from "Build/Extensions/Connection-Services/ServiceConnectionRetrieverService";
import { ConnectionList } from "Build/Extensions/GitHub/ConnectionList";
import { PersonalAccessTokenPanel } from "Build/Extensions/GitHubEnterprise/PersonalAccessTokenPanel";
import * as Resources from "Build/Extensions/GitHubEnterprise/Resources";
import { checkProjectLevelManagePermission, Constant, createEndpointForUserAuth, getServiceEndpointType } from "Build/Extensions/GitHubEnterprise/ServiceConnections";

import * as DesignerResources from "Build/Designer/Resources";

import { ITfsPageService } from "Tfs/Platform/Page";

import { format } from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";

import { Button } from "VSSUI/Button";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";

import { ServiceEndpoint, ServiceEndpointType } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";

export interface Props {
    serviceName: string;
    sourceProvider: string;
    onSelectConnection: (serviceConnectionId: string) => void;
    onErrorMessage: (errorMessage: string) => void;
}

interface State {
    showConnections: boolean;
    serviceConnections: ServiceConnectionInfo[];
    projectId: string;
    isPanelExpanded: boolean;
    serviceEndpointType?: ServiceEndpointType;
}

export class ConnectionSelection extends VssComponent<Props, State, IVssComponentContext> {
    constructor(props: Props, context: IVssComponentContext) {
        super(props, context);

        const pageService = context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const pageData = pageService.getData();
        this.state = {
            showConnections: false,
            serviceConnections: [],
            projectId: pageData!.project!.id,
            isPanelExpanded: false
        };
    }

    public componentDidMount(): void {
        const serviceConnectionRetrieverService = this.context.pageContext.getService<IServiceConnectionRetrieverService>("IServiceConnectionRetrieverService");
        serviceConnectionRetrieverService.subscribe(this._handleConnectionsUpdated, Actions.getServiceConnections);
        serviceConnectionRetrieverService.subscribe(this._handleError, Actions.error);
        serviceConnectionRetrieverService.updateServiceConnections(this.props.sourceProvider);

        getServiceEndpointType(this.context)
            .then((serviceConnectionType: ServiceEndpointType) => this.setState({serviceEndpointType: serviceConnectionType}))
        this._hasProjectLevelManagePermission = checkProjectLevelManagePermission(this.context, this.state.projectId);
    }

    public componentWillUnmount(): void {
        const serviceConnectionRetrieverService = this.context.pageContext.getService<IServiceConnectionRetrieverService>("IServiceConnectionRetrieverService");
        serviceConnectionRetrieverService.unsubscribe(this._handleConnectionsUpdated, Actions.getServiceConnections);
        serviceConnectionRetrieverService.unsubscribe(this._handleError, Actions.error);
    }

    public render(): JSX.Element {
        const hasConnections: boolean = (this.state && this.state.serviceConnections && this.state.serviceConnections.length > 0);
        let content: JSX.Element | null = null;
        if (this.state.showConnections) {
            if (hasConnections) {
                content = (
                    <div className="github-enterprise-connection-selection">
                    {
                        this._renderHeader()
                    }
                    {
                        this.state.showConnections && hasConnections && (
                            <div className="section">
                                <div className="section-header title-s">{Resources.UseExistingConnection}</div>
                                <ConnectionList
                                    showConnections={this.state.showConnections}
                                    serviceConnections={this.state.serviceConnections}
                                    getItemPrimaryText={this._getItemPrimaryText}
                                    getItemSecondaryText={this._getItemSecondaryText}
                                    onSelectConnection={this.props.onSelectConnection} />
                            </div>
                        )
                    }
                    {
                        <div className="section">
                            <div className="section-header title-s">{Resources.CreateNewConnection}</div>
                            <Button
                                primary={!hasConnections}
                                onClick={this._handlePanelExpanded}
                                text={Resources.NewConnection} />
                        </div>
                    }
                </div>
                );
            }
            else {
                content = (
                    <div>
                        {
                            <ZeroData
                                primaryText={Resources.ConnectToGitHubTitle}
                                imageAltText={Resources.ImageOfNoConnections}
                                imagePath={getAssetLocation(this.context.pageContext, noConnectionsPath)}
                                actionText={Resources.ConnectToGitHub}
                                actionType={ZeroDataActionType.ctaButton}
                                onActionClick={this._handlePanelExpanded}
                            />
                        }
                    </div>
                );
            }
        }
        else {
            // We haven't retrieved connections yet, don't show any content
            content = null;
        }

        return (
            <div>
                {
                    content
                }
                {
                    this.state.isPanelExpanded &&
                    (this.state.serviceEndpointType
                        ? <WrappedComponent
                            dependencies={["ms.vss-distributedtask-web.dt-serviceendpoints-editor"]}
                            wrappedType={"endpoints-editor-panel"}
                            onPrimaryAction = {(serviceEndpoint: ServiceEndpoint) => this.props.onSelectConnection(serviceEndpoint.id)}
                            contentType="CreateForm"
                            onDismiss={() => this._handlePanelDismissed()}
                            panelTitle={Resources.NewConnectionPanelTitle}
                            endpointType={this.state.serviceEndpointType}
                            hideBackButton={true} 
                            disableAllPipelinesSetting = { !this._hasProjectLevelManagePermission }
                            />
                        : <PersonalAccessTokenPanel
                            createEndpoint={this._createEndpoint}
                            onDismiss={this._handlePanelDismissed} />)
                }
            </div>
        );
    }

    private _handlePanelExpanded = (): void => {
        this.setState({
            isPanelExpanded: true
        });
    }

    private _handlePanelDismissed = (): void => {
        this.setState({
            isPanelExpanded: false
        });
    }

    private _renderHeader(): JSX.Element {
        return <div className="header-section">
            <div className="subheader body-m secondary-text">{DesignerResources.NewPipeline}</div>
            <div className="header title-l">{Resources.SelectRepositoryTitle}</div>
            <div className="connection-subheader title-s">{ format(Resources.GrantPermission, this.props.serviceName) }</div>
        </div>;
    }

    private _getItemPrimaryText = (connection: ServiceConnectionInfo): string => {
        if (connection) {
            if (connection.name.indexOf(connection.hostname) === -1) {
                return connection.name + " : " + connection.hostname;
            }
            // Hostname is at least part of the name, strip http(s):// to make it a little prettier
            var regex = new RegExp(`/http:\/\/${connection.hostname}|https:\/\/${connection.hostname}`, 'g');
            return connection.name.replace(regex, connection.hostname);
        }
        return Resources.Unknown;
    }

    private _getItemSecondaryText = (connection: ServiceConnectionInfo): string => {
        if (connection && connection.scheme) {
            // GHE uses "Token" for personal access token connections
            if (connection.scheme === Constant.TokenScheme) {
                return Resources.PersonalAccessToken;
            } else if (connection.scheme === Constant.UsernamePasswordScheme) {
                return Resources.UsernamePassword;
            } else {
                return connection.scheme;
            }
        }
        return Resources.Unknown;
    }

    private _createEndpoint = (serverUrl: string, token: string, acceptUntrustedSslCerts: boolean): Promise<void> => {
        return createEndpointForUserAuth(
            this.context,
            this.props.sourceProvider,
            serverUrl,
            acceptUntrustedSslCerts,
            this.state.projectId,
            this.state.serviceConnections,
            { personalAccessToken: token })
            .then(serviceEndpoint => {this.props.onSelectConnection(serviceEndpoint.id)});
    }

    private _handleConnectionsUpdated = (serviceConnections: ServiceConnectionInfo[]): void => {
        this.setState({
            showConnections: true,
            serviceConnections: serviceConnections,
        })
    }

    private _handleError = (e: any) => {
        if (e && e.message) {
            this.props.onErrorMessage(e.message);
        } else {
            this.props.onErrorMessage(e);
        }
    }

    private _hasProjectLevelManagePermission: boolean;
}
